/**
 * One tab of Mike's workbook: his rows, his years, his formulas - inputs editable.
 *
 * A cell a fix changed shows its formula on hover, like any other; the fixes are part of
 * the model here, not a separate list to read.
 */

import { useMemo, useState } from 'react';
import clsx from 'clsx';
import { useMAccountingStore, type MCell, type MTab } from '../../../store/m-accounting';
import { RowLabel } from '../table/RowHint';
import { BaselineHints } from './mHints';

export function formatMoney(value: MCell['value'] | number | undefined): string {
  if (value === null || value === undefined || value === '') return '';
  if (typeof value === 'string') return value;
  if (Math.abs(value) < 1 && value !== 0) return value.toLocaleString(undefined, { maximumFractionDigits: 4 });
  const rounded = Math.round(value);
  if (rounded === 0) return '–';
  const text = Math.abs(rounded).toLocaleString();
  return rounded < 0 ? `(${text})` : text;
}

function InputCell({ cell, onCommit }: { cell: MCell; onCommit: (value: number | null) => void }) {
  const [draft, setDraft] = useState<string | null>(null);
  const shown = draft ?? (typeof cell.value === 'number' && cell.value !== 0 && !cell.formula ? String(cell.value) : '');
  const commit = () => {
    if (draft === null) return;
    const trimmed = draft.replace(/,/g, '').trim();
    const parsed = trimmed === '' ? null : Number(trimmed);
    setDraft(null);
    if (parsed === null || Number.isFinite(parsed)) onCommit(parsed);
  };
  return (
    <input
      className="w-full rounded border border-blue-200 bg-blue-50/60 px-1 py-0.5 text-right font-mono text-xs focus:border-blue-500 focus:outline-none"
      value={shown}
      placeholder={cell.formula ? formatMoney(cell.value) : ''}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
        if (e.key === 'Escape') setDraft(null);
      }}
      title={cell.formula ? `Calculated by default (${cell.formula}) - enter a figure to replace it` : 'Input'}
    />
  );
}

export function MGrid({ tab, historical }: { tab: MTab; historical: number[] }) {
  const setInput = useMAccountingStore((s) => s.setInput);
  const computed = useMAccountingStore((s) => s.computed);
  // A cell that cannot be typed in says why, and what to change instead.
  const hints = useMemo(() => (computed ? new BaselineHints(computed) : null), [computed]);
  const firstHistorical = Math.min(...historical);
  return (
    <div className="overflow-auto rounded-lg border border-gray-200 bg-white">
      <table className="min-w-full border-collapse text-xs">
        <thead className="sticky top-0 z-10 bg-gray-50">
          <tr>
            <th className="sticky left-0 z-20 bg-gray-50 px-3 py-2 text-left font-semibold">{tab.name.replace('(AI) ', '')}</th>
            {tab.columns.map((c) => (
              <th key={c.col} className="px-3 py-2 text-right font-semibold">
                <div>{c.label ?? c.year ?? 'Value'}</div>
                {c.year !== null && (
                  <div className="text-[10px] font-normal uppercase tracking-wide text-gray-500">
                    {historical.includes(c.year) ? 'Historical' : c.year < firstHistorical ? 'Opening' : 'Forecast'}
                  </div>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {tab.rows.map((row) => {
            const heading = Object.keys(row.cells).length === 0;
            return (
              <tr key={row.row} className={clsx('border-t border-gray-100', heading && 'bg-gray-50/60')}>
                <td className={clsx('sticky left-0 bg-white px-3 py-1', heading ? 'pt-3 font-semibold' : 'pl-5')}>
                  {heading ? row.label : <RowLabel label={row.label} hint={hints?.forRow(tab, row) ?? null} />}
                </td>
                {tab.columns.map((c) => {
                  const cell = row.cells[c.col];
                  if (!cell) return <td key={c.col} />;
                  return (
                    <td key={c.col} className="px-2 py-0.5 text-right font-mono">
                      {cell.input ? (
                        <InputCell cell={cell} onCommit={(v) => setInput(tab.sheet, `${c.col}${row.row}`, v)} />
                      ) : (
                        <span title={cell.formula ?? undefined}>{formatMoney(cell.value)}</span>
                      )}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
